import { cn } from '@cobliteam/ui/utils';
import type { Transition, Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect } from 'react';

export interface FileChartIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface FileChartIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const defaultTransition: Transition = {
  duration: 1.2,
  ease: 'easeInOut',
};

const firstLineVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      ...defaultTransition,
      delay: 0.1,
      duration: 0.3,
    },
  },
};

const secondLineVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      ...defaultTransition,
      delay: 0.4,
      duration: 0.3,
    },
  },
};

const thirdLineVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      ...defaultTransition,
      delay: 0.7,
      duration: 0.3,
    },
  },
};

const FileChartColumnIncreasingIcon = forwardRef<FileChartIconHandle, FileChartIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
  const firstLineControls = useAnimation();
  const secondLineControls = useAnimation();
  const thirdLineControls = useAnimation();

  useEffect(() => {
    if (isHovered) {
      firstLineControls.start('animate');
      secondLineControls.start('animate');
      thirdLineControls.start('animate');
    } else {
      firstLineControls.start('normal');
      secondLineControls.start('normal');
      thirdLineControls.start('normal');
    }
  }, [isHovered, firstLineControls, secondLineControls, thirdLineControls]);

  return (
    <div ref={ref as any} className={cn('relative overflow-hidden', className)} style={{ width: size, height: size }} {...props}>
      <svg
        data-testid='lucide-file-chart-column-increasing'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='lucide lucide-file-chart-column-increasing-icon lucide-file-chart-column-increasing'
      >
        <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' />
        <path d='M14 2v4a2 2 0 0 0 2 2h4' />

        <motion.path d='M8 18v-2' variants={firstLineVariants} animate={firstLineControls} />
        <motion.path d='M12 18v-4' variants={secondLineVariants} animate={secondLineControls} />
        <motion.path d='M16 18v-6' variants={thirdLineVariants} animate={thirdLineControls} />
      </svg>
    </div>
  );
});

FileChartColumnIncreasingIcon.displayName = 'FileChartColumnIncreasingIcon';

export { FileChartColumnIncreasingIcon };
